import { Elements } from "@stripe/react-stripe-js";
import { loadStripe } from "@stripe/stripe-js";
import { useCallback, useState } from "react";
import { Badge, Button, Card, Form, FormControl, InputGroup, Modal, Stack } from "react-bootstrap"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom";
import { useApi } from "../api";
import { ICargo } from "../interfaces";
import { ErrorDialog } from "./ErrorDialog";
import { ErrorResponseDialog } from "./ErrorResponseDialog";
import { InjectedCheckoutForm } from "./InjectedCheckoutForm";
import { SuccessDialog } from "./SuccessDialog";

const stripePromise = loadStripe(window.location.hostname === "localhost" ? 'pk_test_70OCDjwDvtq6KIznGEYCB9l8' : 'pk_live_8x88Bq3MeZPoybzdXtNMDMpl');

export const NftCard = (props: any) => {
  const { t } = useTranslation()
  const projectCode = 'SAI'
  const api = useApi(projectCode)
  const navi = useNavigate()
  const [show, setShow] = useState(false)
  const [isLoading, setLoading] = useState(false)
  const [otp, setOtp] = useState<string | null>(null)

  const paymentMethods = ['gas', 'nnego', 'creditCard', 'usdt', 'eth']
  const [paymentMethod, setPaymentMethod] = useState<string | null>(null)

  const handleClose = () => setShow(false);
  const handleShow = () => setShow(true)

  const asset = props.asset
  const image: string = asset['image'];
  const imageUrl = image.indexOf('http') > -1 ? image : `https://file.sup.me.uk/nft/${image}`

  const handlePaymentResponse = useCallback((cargo: ICargo) => {
    setLoading(false)
    if (cargo.returnCode === '000000') {
      SuccessDialog({ title: t('success') })
    } else {
      ErrorResponseDialog(cargo, t)
    }
  }, [t])

  if (asset == null) {
    return <></>
  }

  if (imageUrl == null) {
    return <></>
  }

  return (
    <>
      <Card border='light' className='border shadow mx-3 mb-5 rounded-md'>
        <Card.Img variant="top" src={imageUrl} />
        <Card.Body>
          <Card.Title className="mb-0 text-rose-500">{asset['name']}</Card.Title>
          <div>
            <div className="text-gray-400 text-xs mb-3">
            </div>
            <hr />
            <div className="text-gray-700 text-sm">{asset['description']}</div>
            <div className="text-gray-400 text-xs bg-slate-200 rounded-lg p-3 pt-2 text-center mt-3">
              <div className="text-right text-sm mb-1">
                <Badge pill bg="secondary">
                  {asset['chainType']}
                </Badge>
              </div>
              <div>{asset['contractAddress'] || asset['nftId']}</div>
            </div>
            <div className="text-right pt-3 text-purple-800 cursor-pointer" onClick={() => {
              navi(`/web/asset/detail/${asset['nftId']}`)
            }}>{t('detail')}</div>
          </div>
        </Card.Body>
        {
          asset['contractAddress']
            ? <Card.Footer>
              <Stack direction="horizontal" gap={1}>
                <div className="text-sm">Open Sea</div>
                <div className="ms-auto text-sm"></div>
                <div className="vr" />
                <div className="bg-light border text-sm p-1 px-3 rounded cursor-pointer" onClick={() => {
                  window.location.href = `https://opensea.io/assets/ethereum/${asset['contractAddress']}/1`
                }}>{t('view')}</div>
              </Stack>
            </Card.Footer>
            : <Card.Footer>
              <Stack direction="horizontal" gap={1}>
                <div className="text-sm">{t('price')}</div>
                <div className="ms-auto text-sm">{new Intl.NumberFormat('en').format(asset['sellingPrice'])}</div>
                <div className="vr" />
                <div className="bg-light border text-sm p-1 px-3 rounded cursor-pointer" onClick={() => {
                  handleShow()
                }}>{t('buy')}</div>
              </Stack>
            </Card.Footer>
        }
      </Card>
      <Modal
        show={show}
        onHide={handleClose}
        backdrop="static"
        keyboard={false}
        aria-labelledby="contained-modal-title-vcenter"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title className="text-rose-500">{asset['name']}</Modal.Title>
        </Modal.Header>
        <Modal.Body className="text-gray-500">
          <div>
            <span>{t('buyingPrice')} : </span>
            <span className="text-rose-500 font-bold">{new Intl.NumberFormat('en').format(asset['sellingPrice'])}</span>
          </div>
          <div className="mt-3">
            <div>Payment Method</div>
            <Form className="ml-3 text-sm">
              {
                paymentMethods.map(method => {
                  return (
                    <Form.Check
                      key={method}
                      type='radio'
                      name='paymentMethod'
                      label={t(method)}
                      onClick={() => {
                        setPaymentMethod(method)
                      }}
                      disabled={method === 'usdt' || method === 'eth'}
                    />
                  )
                })
              }
            </Form>
            {
              (paymentMethod === 'gas' || paymentMethod === 'nnego') && <Form.Group className="mt-3">
                <Form.Label>{t('oneTimePasscode')}</Form.Label>
                <InputGroup>
                  <FormControl
                    type="password"
                    onChange={(e) => {
                      setOtp(e.target.value)
                    }}
                    aria-label={t('pleaseInputOtp')}
                  />
                  <Button variant="outline-danger" disabled={isLoading} onClick={() => {
                    setLoading(true)
                    api.sendOtp((cargo: ICargo) => {
                      setLoading(false)
                      if (cargo.returnCode === '000000') {
                        SuccessDialog({ title: t('otpSentSuccess') })
                      } else {
                        switch (cargo.returnCode) {
                          case '100003':
                            ErrorDialog({ title: t(`errorCode.${cargo.returnCode}`) })
                            window.location.href = '/login.html'
                            break;
                          default:
                            ErrorDialog({ title: t('otpSentFailure') })
                        }
                      }
                    })
                  }}>{t('sendOtp')}</Button>
                </InputGroup>
              </Form.Group>
            }
            {
              paymentMethod === 'creditCard' && <div className="mt-3">
                <Elements stripe={stripePromise}>
                  <InjectedCheckoutForm projectCode={projectCode} product='nftTrading' nftId={asset['nftId']} amount={asset['sellingPrice'] || 0} onSuccess={() => {
                    setShow(false)
                  }} />
                </Elements>
              </div>
            }
          </div>
        </Modal.Body>
        <Modal.Footer>
          {
            paymentMethod !== 'creditCard' && <Button variant="primary" disabled={isLoading} onClick={() => {
              if (paymentMethod === null) {
                ErrorDialog({ title: t('pleaseSelectPaymentMethod') })
                return
              }
              if (otp === null) {
                ErrorDialog({ title: t('pleaseInputOtp') })
                return
              }
              if (paymentMethod === 'gas') {
                setLoading(true)
                api.tradingNftByGas(asset['nftId'], otp, handlePaymentResponse)
              }
              if (paymentMethod === 'nnego') {
                setLoading(true)
                api.tradingNftByNnego(asset['nftId'], otp, handlePaymentResponse)
              }
            }}>{isLoading ? t('processing') : t('checkout')}</Button>
          }
        </Modal.Footer>
      </Modal>
    </>
  )
}